import React from "react";
import "@/App.css";
import "@/styles/goal-theme.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import IdleTimeout from "@/components/app/IdleTimeout";
import LoginPage from "@/pages/Login";
import Register from "@/pages/Register";
import Verify from "@/pages/Verify";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClientDetail from "@/pages/AdminClientDetail";
import AdminUsers from "@/pages/AdminUsers";
import AdminFeed from "@/pages/AdminFeed";
import AdminMeetings from "@/pages/AdminMeetings";
import AdminSettings from "@/pages/AdminSettings";
import AdminTicker from "@/pages/AdminTicker";
import AdminIntegrations from "@/pages/AdminIntegrations";
import WarRoom from "@/pages/WarRoom";
import PublicRoom from "@/pages/PublicRoom";
import AdminServiceRequests from "@/pages/AdminServiceRequests";
import TradelineAdminDashboard from "@/pages/TradelineAdminDashboard";
import ClientReferrals from "@/pages/ClientReferrals";
import ReferralShortLink from "@/pages/ReferralShortLink";
import AdminWorkQueue from "@/pages/AdminWorkQueue";
import AdminGoalInsights from "@/pages/AdminGoalInsights";
import AdminPartnerLeads from "@/pages/AdminPartnerLeads";
import QuackCityPage from "@/pages/QuackCityPage";
import ShopPage, { ShopDirectory } from "@/pages/ShopPage";
import QuackLabs from "@/pages/QuackLabs";
import ArcadePage from "@/pages/ArcadePage";
import City3DPage from "@/pages/City3DPage";
import ClientServices from "@/pages/ClientServices";
import ClientServiceCheckout from "@/pages/ClientServiceCheckout";
import ClientServiceRequests from "@/pages/ClientServiceRequests";
import ClientTradelines from "@/pages/ClientTradelines";
import ClientTradelineReservations from "@/pages/ClientTradelineReservations";
import AdminSchedule from "@/pages/AdminSchedule";
import AdminBulkScheduler from "@/pages/AdminBulkScheduler";
import AdminSMS from "@/pages/AdminSMS";
import ClientMessages from "@/pages/ClientMessages";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientMeetings from "@/pages/ClientMeetings";
import FundingFeed from "@/pages/FundingFeed";
import FundingCenter from "@/pages/FundingCenter";
import LoanPrograms from "@/pages/LoanPrograms";
import DocsHub from "@/pages/DocsHub";
import DocPage from "@/pages/DocPage";
import HowItWorks from "@/pages/HowItWorks";
import AiHelpDrawer from "@/components/app/AiHelpDrawer";
import WalkthroughTour from "@/components/app/WalkthroughTour";
import AuthCallback from "@/pages/AuthCallback";
import AdminLive from "@/pages/AdminLive";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import useMessagePing from "@/hooks/useMessagePing";
import SplashScreen from "@/components/app/SplashScreen";
import SecretAccessModal from "@/components/app/SecretAccessModal";
import KonamiCode from "@/components/app/KonamiCode";
import SlowDownEgg from "@/components/app/SlowDownEgg";
import DuckyCoach from "@/components/app/DuckyCoach";
import { GoalProvider } from "@/context/GoalContext";
import Home from "@/pages/Home";
import QuackCity from "@/pages/QuackCity";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const STAFF_ROLES = ["super_admin", "admin", "credit_specialist", "team_member"];
const ADMIN_ROLES = ["super_admin", "admin"];
const SUPER_ADMIN_ROLES = ["super_admin"];
const TRADELINE_ROLES = ["tradeline_admin", "super_admin", "admin"];

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null; // still loading
  let gameMode = false;
  try { gameMode = localStorage.getItem("cq_game_mode") === "1"; } catch {}
  if (user && user.role === "client" && gameMode) return <Navigate to="/city" replace />;
  if (user && user.role === "client") return <Navigate to="/portal" replace />;
  if (user && user.role === "tradeline_admin") return <Navigate to="/tradeline-admin" replace />;
  if (user && ADMIN_ROLES.includes(user.role)) return <Navigate to="/admin" replace />;
  if (user && STAFF_ROLES.includes(user.role)) return <Navigate to="/admin" replace />;
  return <Home />;
}

function AppInner() {
  usePresenceHeartbeat();
  useMessagePing();
  // Global referral catcher — any URL like /?ref=MARKE or /portal?ref=JENNA
  // persists the code so the register form + backend attribute the signup.
  React.useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ref = (p.get("ref") || "").trim().toUpperCase();
      if (ref) {
        localStorage.setItem("cq_ref", ref);
        // Fire-and-forget click track (endpoint is public)
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/track-click`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: ref, path: window.location.pathname }),
        }).catch(() => {});
      }
    } catch {}
  }, []);
  const [splashDone, setSplashDone] = React.useState(() => {
    try { return sessionStorage.getItem("cq_splash_seen") === "1"; } catch { return true; }
  });
  const dismiss = () => {
    try { sessionStorage.setItem("cq_splash_seen", "1"); } catch {}
    setSplashDone(true);
  };
  return (
    <>
      {!splashDone && <SplashScreen onDone={dismiss} />}
      <SecretAccessModal />
      <KonamiCode />
      <SlowDownEgg />
      <AiHelpDrawer />
      <WalkthroughTour />
      <DuckyCoach />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <GoalProvider>
          <BrowserRouter>
            <IdleTimeout />
            <AppRoutes />
            <AppInner />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </GoalProvider>
      </AuthProvider>
    </div>
  );
}

function AppRoutes() {
  // Synchronously handle Google OAuth callback BEFORE routes/protected routes fire.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  const location = useLocation();
  if ((location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/city" element={<QuackCity />} />
            <Route path="/city3d" element={<City3DPage />} />
            <Route path="/arcade" element={<ArcadePage />} />
            <Route path="/quackcity" element={<QuackCityPage />} />
            <Route path="/shop" element={<ShopDirectory />} />
            <Route path="/labs" element={<QuackLabs />} />
            <Route path="/shop/:handle" element={<ShopPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/r/:code" element={<ReferralShortLink />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={STAFF_ROLES}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/clients/:id" element={<ProtectedRoute roles={STAFF_ROLES}><AdminClientDetail /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/feed" element={<ProtectedRoute roles={STAFF_ROLES}><AdminFeed /></ProtectedRoute>} />
            <Route path="/admin/meetings" element={<ProtectedRoute roles={STAFF_ROLES}><AdminMeetings /></ProtectedRoute>} />
            <Route path="/admin/schedule" element={<ProtectedRoute roles={STAFF_ROLES}><AdminSchedule /></ProtectedRoute>} />
            <Route path="/admin/bulk-schedule" element={<ProtectedRoute roles={STAFF_ROLES}><AdminBulkScheduler /></ProtectedRoute>} />
            <Route path="/admin/sms" element={<ProtectedRoute roles={STAFF_ROLES}><AdminSMS /></ProtectedRoute>} />
            <Route path="/admin/live" element={<ProtectedRoute roles={STAFF_ROLES}><AdminLive /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/ticker" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminTicker /></ProtectedRoute>} />
            <Route path="/admin/integrations" element={<ProtectedRoute roles={SUPER_ADMIN_ROLES}><AdminIntegrations /></ProtectedRoute>} />
            <Route path="/admin/warroom" element={<ProtectedRoute roles={STAFF_ROLES}><WarRoom /></ProtectedRoute>} />
            <Route path="/admin/service-requests" element={<ProtectedRoute roles={STAFF_ROLES}><AdminServiceRequests /></ProtectedRoute>} />
            <Route path="/admin/work-queue" element={<ProtectedRoute roles={STAFF_ROLES}><AdminWorkQueue /></ProtectedRoute>} />
            <Route path="/admin/tradelines" element={<Navigate to="/tradeline-admin" replace />} />
            <Route path="/tradeline-admin" element={<ProtectedRoute roles={TRADELINE_ROLES}><TradelineAdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/goal-insights" element={<ProtectedRoute roles={STAFF_ROLES}><AdminGoalInsights /></ProtectedRoute>} />
            <Route path="/admin/partner-leads" element={<ProtectedRoute roles={STAFF_ROLES}><AdminPartnerLeads /></ProtectedRoute>} />
            <Route path="/portal/services" element={<ProtectedRoute roles={["client"]}><ClientServices /></ProtectedRoute>} />
            <Route path="/portal/services/checkout" element={<ProtectedRoute roles={["client"]}><ClientServiceCheckout /></ProtectedRoute>} />
            <Route path="/portal/my-service-requests" element={<ProtectedRoute roles={["client"]}><ClientServiceRequests /></ProtectedRoute>} />
            <Route path="/portal/referrals" element={<ProtectedRoute><ClientReferrals /></ProtectedRoute>} />
            <Route path="/portal/tradelines" element={<ProtectedRoute roles={["client"]}><ClientTradelines /></ProtectedRoute>} />
            <Route path="/portal/tradelines/reservations" element={<ProtectedRoute roles={["client"]}><ClientTradelineReservations /></ProtectedRoute>} />
            <Route path="/portal/messages" element={<ProtectedRoute roles={["client"]}><ClientMessages /></ProtectedRoute>} />
            <Route path="/portal" element={<ProtectedRoute roles={["client"]}><ClientDashboard /></ProtectedRoute>} />
            <Route path="/portal/feed" element={<ProtectedRoute><FundingFeed /></ProtectedRoute>} />
            <Route path="/portal/funding" element={<ProtectedRoute roles={["client"]}><FundingCenter /></ProtectedRoute>} />
            <Route path="/portal/funding/loan-programs" element={<ProtectedRoute roles={["client"]}><LoanPrograms /></ProtectedRoute>} />
            <Route path="/portal/meetings" element={<ProtectedRoute roles={["client"]}><ClientMeetings /></ProtectedRoute>} />
            <Route path="/docs" element={<DocsHub />} />
            <Route path="/room/:id" element={<PublicRoom />} />
            <Route path="/docs/how-it-works" element={<HowItWorks />} />
            <Route path="/docs/:slug" element={<DocPage />} />
            <Route path="/help" element={<Navigate to="/docs" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}
